/* The header for a screen that renders OUTSIDE the workspace shell.
 *
 * Two screens do: `/account`, because an account belongs to no Organisation,
 * and `/plans`, because it is a sales page that must render with no session.
 * Both are correct decisions and both had the same consequence — the navigation
 * vanished and there was no way back except the browser button.
 *
 * A back link rather than restoring the whole sidebar. These screens genuinely
 * are outside a workspace: showing eight workspace destinations on a page that
 * belongs to none of them would be claiming a context that does not apply, and
 * `aria-current` would have nothing true to say.
 *
 * ── where "back" goes, and why it is not `history.back()` ────────────────────
 * A history step is not a destination: it lands somewhere different depending
 * on how the screen was reached, and after a redirect from a payment provider
 * it goes back to the provider. So the target is resolved from what is known —
 * the named workspace, else the reader's first Membership, else the chooser.
 */

import { Link } from '@tanstack/react-router';
import { useCurrentUser } from '../../features/auth/index.js';
import { useTranslate } from '../i18n/index.js';

export interface StandaloneHeaderProps {
  /** The workspace this screen was opened from, when one is named. */
  readonly workspace?: string | undefined;
  readonly title: string;
}

export function StandaloneHeader({ workspace, title }: StandaloneHeaderProps) {
  const t = useTranslate();
  const { data: user } = useCurrentUser();

  /* The named workspace wins; otherwise the first Membership, which is the one
   * the chooser would land on anyway. Signed out there is neither, and the
   * link is not rendered at all rather than pointing somewhere that will bounce
   * them to sign-in — an offer that cannot be honoured is worse than none. */
  const target = workspace ?? user?.memberships[0]?.organisationId;

  return (
    <div className="standalone__head">
      {target === undefined ? (
        <span className="standalone__brand">{t('app.name')}</span>
      ) : (
        <Link
          className="back"
          to="/orgs/$organisationId/calendar"
          params={{ organisationId: target }}
        >
          {t('plans.back_to_workspace')}
        </Link>
      )}
      <h1 className="standalone__title">{title}</h1>
    </div>
  );
}
